import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { setPlatformFavicon } from "@/contexts/TenantThemeContext";
import {
  HEADING_FONTS, BODY_FONTS, ROUNDING_OPTIONS, useTenantTheme, FONT_SIZE_KEY, applyFontSize,
} from "@/contexts/TenantThemeContext";
import {
  PaintBrushBroad, TextAa, ArrowsOut, Calendar, Palette, CheckCircle,
  FloppyDisk, ArrowCounterClockwise, CircleNotch, Warning, ArrowsClockwise,
} from "@phosphor-icons/react";

// Default reservation source palette (also duplicated in backend `Tenant`).
// Used to reset a single row + as fallback when tenant hasn't set a color.
const DEFAULT_SOURCE_COLORS = {
  booking: "#003580",
  airbnb: "#FF385C",
  expedia: "#FFC72C",
  direct: "#0E9F6E",
  booking_engine: "#4F46E5",
  other: "#6B7280",
};
const SOURCE_LABELS = {
  booking: "Booking.com",
  airbnb: "Airbnb",
  expedia: "Expedia",
  direct: "Direktna",
  booking_engine: "Booking Engine",
  other: "Ostali izvori",
};

const FONT_WEIGHTS = [
  { value: "400", label: "400 – Regular" },
  { value: "500", label: "500 – Medium" },
  { value: "600", label: "600 – Semi Bold" },
  { value: "700", label: "700 – Bold" },
  { value: "800", label: "800 – Extra Bold" },
];

function Section({ icon: Icon, title, children }) {
  return (
    <section className="rounded-lg border border-border bg-card p-4 sm:p-5">
      <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-foreground">
        <Icon size={18} weight="duotone" className="text-primary" />
        {title}
      </h2>
      {children}
    </section>
  );
}

function AppearancePicker() {
  const { mode, setMode } = useTheme();
  return (
    <div className="flex flex-wrap gap-2" data-testid="appearance-picker">
      {[{ v: "light", l: "Svijetlo" }, { v: "dark", l: "Tamno" }, { v: "system", l: "Automatski" }].map((o) => (
        <button key={o.v} type="button" onClick={() => setMode(o.v)} className={`rounded-md border px-4 py-2 text-sm font-medium ${mode === o.v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-muted"}`} data-testid={`appearance-${o.v}`}>
          {o.l}
        </button>
      ))}
      <p className="w-full text-[11px] text-muted-foreground">Automatski prati postavku mobitela ili računala. Boje brenda ostaju iste, mijenjaju se samo pozadine i tekst.</p>
    </div>
  );
}

function FontSizePicker() {
  const [val, setVal] = useState(() => localStorage.getItem(FONT_SIZE_KEY) || "normal");
  const pick = (v) => { setVal(v); localStorage.setItem(FONT_SIZE_KEY, v); applyFontSize(v); };
  return (
    <div className="flex flex-wrap gap-2" data-testid="font-size-picker">
      {[{ v: "small", l: "Mala", c: "text-xs" }, { v: "normal", l: "Normalna", c: "text-sm" }, { v: "large", l: "Velika", c: "text-base" }].map((o) => (
        <button key={o.v} type="button" onClick={() => pick(o.v)} className={`rounded-md border px-4 py-2 font-medium ${o.c} ${val === o.v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-muted"}`} data-testid={`font-size-${o.v}`}>
          {o.l}
        </button>
      ))}
      <p className="w-full text-[11px] text-muted-foreground">Sprema se na ovom uređaju (mobitel i računalo mogu imati različitu veličinu).</p>
    </div>
  );
}

function FieldLabel({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="mt-1 block truncate text-[11px] text-muted-foreground" title={hint}>{hint}</span>}
    </label>
  );
}

function ColorRow({ label, hint, value, onChange, testId }) {
  return (
    <div className="grid grid-cols-[auto,minmax(0,1fr),auto] items-center gap-3 px-3 py-2.5" data-testid={testId}>
      <label className="relative h-9 w-9 shrink-0 cursor-pointer overflow-hidden rounded-full border border-border shadow-inner" style={{ background: value || "#000000" }}>
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          data-testid={`${testId}-picker`}
        />
      </label>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground">{label}</p>
        {hint && <p className="truncate text-[11px] text-muted-foreground" title={hint}>{hint}</p>}
      </div>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-[92px] rounded-md border border-border bg-background px-2 text-center font-mono text-xs uppercase text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        data-testid={`${testId}-hex`}
      />
    </div>
  );
}

export default function ThemePage() {
  const { tenant, user, refreshTenant } = useAuth();
  const { preview, clearPreview } = useTenantTheme();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (tenant && !form) setForm({ ...tenant });
  }, [tenant, form]);

  // Live preview on form change
  useEffect(() => {
    if (form) preview(form);
    return () => {};
  }, [form, preview]);

  if (!form) {
    return (
      <div className="flex h-64 items-center justify-center">
        <CircleNotch size={28} className="animate-spin text-primary" />
      </div>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      // Only send theme + branding fields (avoid clobbering API keys via masked values)
      const themeKeys = [
        "brand_name", "logo_url", "favicon_url", "badge_url",
        "heading_font", "heading_font_weight", "body_font", "rounding",
        "primary_color", "primary_hover", "primary_dark", "primary_light",
        "accent_color", "dark_color", "dark_hover",
        "bg_page", "bg_light", "bg_white",
        "border_color", "border_light",
        "text_primary", "text_secondary", "text_muted", "text_white",
        "calendar_color",
        "source_colors",
      ];
      const payload = {};
      themeKeys.forEach((k) => { if (form[k] != null) payload[k] = form[k]; });
      await api.put("/tenants/me", payload);
      await refreshTenant();
      clearPreview();
      setSaved(true);
      setTimeout(() => setSaved(false), 3500);
    } catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!confirm("Vratiti sve theme postavke na zadane vrijednosti?")) return;
    setResetting(true);
    try {
      const { data } = await api.post("/tenants/me/theme/reset");
      await refreshTenant();
      setForm({ ...data });
      clearPreview();
    } catch (e) { alert(formatApiError(e)); }
    finally { setResetting(false); }
  };

  return (
    <div className="space-y-5" data-testid="theme-page">
      <div className="flex flex-col gap-1">
        <h1 className="hidden font-heading text-2xl font-bold text-foreground sm:block">Tema i branding</h1>
        <p className="text-sm text-muted-foreground">Promjene se prikazuju odmah, a trajno se spremaju klikom na <strong>Spremi</strong>.</p>
      </div>

      <Section icon={PaintBrushBroad} title="Branding">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FieldLabel label="Brand / Site name">
            <input className="rw-input" value={form.brand_name || ""} onChange={(e) => set("brand_name", e.target.value)} data-testid="theme-brand-name" />
          </FieldLabel>
          <FieldLabel label="Logo URL" hint="Prikazuje se u navigaciji i na booking engine-u. Preferirano: SVG.">
            <input className="rw-input mono text-xs" placeholder="https://..." value={form.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} data-testid="theme-logo-url" />
          </FieldLabel>
          <IconField
            label="Ikona aplikacije (početni zaslon i favicon)"
            hint="Link na SVG ili PNG. Prazno = zadana ikona platforme."
            value={form.favicon_url || ""}
            onChange={(v) => set("favicon_url", v)}
            previewSrc={`/api/app/icon.png?size=96&tenant=${tenant.id}&v=${encodeURIComponent(tenant.favicon_url || "")}`}
            testId="theme-favicon-url"
          />
          <IconField
            label="Mala ikona obavijesti (vrh ekrana)"
            hint="Pretvara se u bijeli obris. Prazno = obris ikone aplikacije."
            value={form.badge_url || ""}
            onChange={(v) => set("badge_url", v)}
            previewSrc={`/api/app/icon.png?kind=badge&size=96&tenant=${tenant.id}&v=${encodeURIComponent((tenant.badge_url || "") + (tenant.favicon_url || ""))}`}
            badge
            testId="theme-badge-url"
          />
        </div>
      </Section>

      {user?.role === "super_admin" && <PlatformFaviconSection />}

      <Section icon={TextAa} title="Veličina teksta (ovaj uređaj)">
        <FontSizePicker />
      </Section>

      <Section icon={PaintBrushBroad} title="Izgled (ovaj uređaj)">
        <AppearancePicker />
      </Section>

      <Section icon={TextAa} title="Tipografija">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FieldLabel label="Font naslova" hint="Za naslove (H1, H2, ...)">
            <select className="rw-input" value={form.heading_font || "Proxima Nova"} onChange={(e) => set("heading_font", e.target.value)} data-testid="theme-heading-font">
              <optgroup label="Serif">{HEADING_FONTS.filter((f) => f.category === "serif").map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}</optgroup>
              <optgroup label="Sans-serif">{HEADING_FONTS.filter((f) => f.category === "sans").map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}</optgroup>
            </select>
          </FieldLabel>
          <FieldLabel label="Debljina naslova" hint="Debljina za naslove">
            <select className="rw-input" value={form.heading_font_weight || "600"} onChange={(e) => set("heading_font_weight", e.target.value)} data-testid="theme-heading-weight">
              {FONT_WEIGHTS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
          </FieldLabel>
          <FieldLabel label="Font teksta" hint="Za paragrafe, gumbe, labele">
            <select className="rw-input" value={form.body_font || "Inter"} onChange={(e) => set("body_font", e.target.value)} data-testid="theme-body-font">
              {BODY_FONTS.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
            </select>
          </FieldLabel>
        </div>
        <div className="mt-4 rounded-md border border-border bg-background p-4">
          <h3 className="font-heading text-2xl text-foreground">Primjer naslova</h3>
          <p className="mt-1 text-sm text-foreground">Ovo je primjer body teksta (sans-serif) koji prikazuje kako će sadržaj izgledati.</p>
        </div>
      </Section>

      <Section icon={ArrowsOut} title="Zaobljenost">
        <p className="mb-3 text-xs text-muted-foreground">Primjenjuje se na gumbove, kartice, input polja i ostale elemente.</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {ROUNDING_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => set("rounding", opt.value)}
              className={`flex flex-col items-center gap-2 border p-4 transition-colors ${form.rounding === opt.value ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/40"}`}
              style={{ borderRadius: opt.radius }}
              data-testid={`theme-rounding-${opt.value}`}
            >
              <div className="h-3 w-20 bg-muted" style={{ borderRadius: opt.radius }} />
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground">{opt.label}</p>
                <p className="text-[11px] text-muted-foreground">{opt.radius}</p>
              </div>
            </button>
          ))}
        </div>
      </Section>

      <Section icon={Calendar} title="Kalendar">
        <div className="overflow-hidden rounded-md border border-border bg-background">
          <ColorRow label="Odabir datuma" hint="Odabrani datumi u kalendaru i pickeru" value={form.calendar_color} onChange={(v) => set("calendar_color", v)} testId="theme-calendar-color" />
        </div>
      </Section>

      {/* Reservation-source palette — colors used for reservation blocks in the calendar */}
      <Section icon={Palette} title="Boje izvora rezervacija">
        <p className="mb-3 truncate text-xs text-muted-foreground">Preporuka: zadrži prepoznatljive boje OTA kanala.</p>
        <div className="divide-y divide-border overflow-hidden rounded-md border border-border bg-background">
          {Object.keys(DEFAULT_SOURCE_COLORS).map((key) => {
            const currentColor = (form.source_colors || {})[key] || DEFAULT_SOURCE_COLORS[key];
            return (
              <ColorRow
                key={key}
                label={SOURCE_LABELS[key]}
                hint={`Zadano: ${DEFAULT_SOURCE_COLORS[key]}`}
                value={currentColor}
                onChange={(v) => set("source_colors", { ...(form.source_colors || DEFAULT_SOURCE_COLORS), [key]: v })}
                testId={`theme-source-${key}`}
              />
            );
          })}
        </div>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={() => set("source_colors", { ...DEFAULT_SOURCE_COLORS })}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
            data-testid="theme-source-reset"
          >
            <ArrowsClockwise size={12} weight="bold" /> Vrati zadane boje
          </button>
        </div>
      </Section>

      <Section icon={Palette} title="Primarne boje">
        <div className="divide-y divide-border overflow-hidden rounded-md border border-border bg-background">
          <ColorRow label="Primary Color" hint="Glavna brand boja — gumbi, linkovi, isticanja" value={form.primary_color} onChange={(v) => set("primary_color", v)} testId="theme-primary" />
          <ColorRow label="Primary Hover" hint="Hover stanje za primarne gumbe" value={form.primary_hover} onChange={(v) => set("primary_hover", v)} testId="theme-primary-hover" />
          <ColorRow label="Primary Dark" hint="Tamnija varijanta za isticanja" value={form.primary_dark} onChange={(v) => set("primary_dark", v)} testId="theme-primary-dark" />
          <ColorRow label="Primary Light" hint="Svjetlija varijanta za suptilne akcente" value={form.primary_light} onChange={(v) => set("primary_light", v)} testId="theme-primary-light" />
        </div>
      </Section>

      <Section icon={Palette} title="Tamne boje">
        <div className="divide-y divide-border overflow-hidden rounded-md border border-border bg-background">
          <ColorRow label="Dark Color" hint="Navbar, headeri, tamne pozadine" value={form.dark_color} onChange={(v) => set("dark_color", v)} testId="theme-dark" />
          <ColorRow label="Dark Hover" hint="Hover stanje za tamne elemente" value={form.dark_hover} onChange={(v) => set("dark_hover", v)} testId="theme-dark-hover" />
        </div>
      </Section>

      <Section icon={Palette} title="Pozadine">
        <div className="divide-y divide-border overflow-hidden rounded-md border border-border bg-background">
          <ColorRow label="Page Background" hint="Glavna pozadinska boja stranica" value={form.bg_page} onChange={(v) => set("bg_page", v)} testId="theme-bg-page" />
          <ColorRow label="Light Background" hint="Pozadina kartica i sekcija" value={form.bg_light} onChange={(v) => set("bg_light", v)} testId="theme-bg-light" />
          <ColorRow label="White Background" hint="Bijela za forme i input polja" value={form.bg_white} onChange={(v) => set("bg_white", v)} testId="theme-bg-white" />
        </div>
      </Section>

      <Section icon={Palette} title="Rubovi">
        <div className="divide-y divide-border overflow-hidden rounded-md border border-border bg-background">
          <ColorRow label="Border Color" hint="Input bordere, card bordere" value={form.border_color} onChange={(v) => set("border_color", v)} testId="theme-border" />
          <ColorRow label="Light Border" hint="Suptilni separatori" value={form.border_light} onChange={(v) => set("border_light", v)} testId="theme-border-light" />
        </div>
      </Section>

      <Section icon={Palette} title="Tekst">
        <div className="divide-y divide-border overflow-hidden rounded-md border border-border bg-background">
          <ColorRow label="Primary Text" hint="Glavni body tekst, naslovi" value={form.text_primary} onChange={(v) => set("text_primary", v)} testId="theme-text-primary" />
          <ColorRow label="Secondary Text" hint="Subnaslovi, opisi" value={form.text_secondary} onChange={(v) => set("text_secondary", v)} testId="theme-text-secondary" />
          <ColorRow label="Muted Text" hint="Hint, placeholderi, labele" value={form.text_muted} onChange={(v) => set("text_muted", v)} testId="theme-text-muted" />
          <ColorRow label="White Text" hint="Tekst na tamnim pozadinama" value={form.text_white} onChange={(v) => set("text_white", v)} testId="theme-text-white" />
        </div>
      </Section>

      {/* Sticky action bar */}
      <div className="sticky bottom-[72px] z-20 flex items-center justify-between gap-2 rounded-xl border border-border bg-card/95 p-2 shadow-lg backdrop-blur lg:bottom-4" data-testid="theme-action-bar">
        <button
          onClick={reset}
          disabled={resetting || saving}
          className="flex h-10 items-center gap-2 whitespace-nowrap rounded-md px-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          data-testid="theme-reset"
        >
          <ArrowCounterClockwise size={14} weight="bold" />
          {resetting ? "Resetiram..." : "Zadane postavke"}
        </button>
        <button
          onClick={save}
          disabled={saving || resetting}
          className="flex h-10 items-center gap-2 whitespace-nowrap rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
          data-testid="theme-save"
          
        >
          <FloppyDisk size={14} weight="duotone" />
          {saving ? "Spremam..." : "Spremi"}
          {saved && <CheckCircle size={14} weight="fill" className="text-white" />}
        </button>
      </div>
      <style>{`.rw-input { width:100%; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.5rem 0.625rem; font-size:0.875rem; border-radius:var(--radius); color:hsl(var(--foreground)); } .rw-input:focus { outline:none; border-color:hsl(var(--primary)); box-shadow:0 0 0 3px hsl(var(--primary)/0.2); }`}</style>
    </div>
  );
}

function IconField({ label, hint, value, onChange, previewSrc, badge, testId }) {
  return (
    <FieldLabel label={label} hint={hint}>
      <div className="flex items-center gap-2">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border ${badge ? "bg-[#001A38]" : "bg-muted"}`} title="Trenutno spremljeno">
          <img src={previewSrc} alt="" className={badge ? "h-6 w-6" : "h-10 w-10 object-contain"} data-testid={`${testId}-preview`} />
        </span>
        <input className="rw-input mono text-xs" placeholder="https://.../logo.svg" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
      </div>
    </FieldLabel>
  );
}

function PlatformFaviconSection() {
  const [form, setForm] = useState({ favicon_url: "", badge_url: "" });
  const [ver, setVer] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/public/platform-branding").then(({ data }) => setForm({ favicon_url: data?.favicon_url || "", badge_url: data?.badge_url || "" })).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/platform-branding", form);
      setPlatformFavicon(data.favicon_url);
      setVer(Date.now());
      toast.success("Zadane ikone platforme spremljene");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Section icon={PaintBrushBroad} title="Zadane ikone platforme (svi korisnici)">
      <p className="mb-3 text-xs text-muted-foreground">Koriste se na prijavi i za sve račune koji u Temi nemaju svoje. Vidljivo samo administratoru platforme. SVG ili PNG link.</p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <IconField label="Ikona aplikacije (početni zaslon i favicon)" hint="Prazno = Lodgy znak." value={form.favicon_url} onChange={(v) => setForm((f) => ({ ...f, favicon_url: v }))} previewSrc={`/api/app/icon.png?size=96&v=${ver}`} testId="platform-favicon-url" />
        <IconField label="Mala ikona obavijesti (vrh ekrana)" hint="Prazno = obris ikone aplikacije." value={form.badge_url} onChange={(v) => setForm((f) => ({ ...f, badge_url: v }))} previewSrc={`/api/app/icon.png?kind=badge&size=96&v=${ver}`} badge testId="platform-badge-url" />
      </div>
      <button type="button" onClick={save} disabled={busy} className="rw-btn-primary rw-btn-sm mt-3" data-testid="platform-favicon-save">{busy ? "Spremam..." : "Spremi zadane ikone"}</button>
    </Section>
  );
}
